import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useTranslation } from '../contexts/LanguageContext';
import { LoadingSpinner } from './ui/loading-spinner';
import { ErrorMessage, EmptyState } from './ui/error-message';
import { 
  BarChart3, 
  TrendingUp, 
  MapPin, 
  FileText, 
  CheckCircle, 
  AlertTriangle,
  Clock,
  Users,
  TreePine,
  IndianRupee,
  Activity,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { translate: t, currentLanguage } = useTranslation();
  const [stats, setStats] = useState(null);
  const [recentClaims, setRecentClaims] = useState([]); // Always initialize as empty array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setError(null);
      const [statsResponse, claimsResponse] = await Promise.all([
        axios.get(`${API}/dashboard/stats`).catch(() => ({ data: getDefaultStats() })),
        axios.get(`${API}/claims?limit=5`).catch(() => ({ data: getDefaultClaims() }))
      ]);
      
      setStats(statsResponse.data);
      // Ensure claimsResponse.data is always an array
      const claimsData = Array.isArray(claimsResponse.data) 
        ? claimsResponse.data 
        : (claimsResponse.data?.claims || []);
      setRecentClaims(claimsData.slice(0, 5));
      
      // Success toast only if data was actually fetched
      if (statsResponse.data && statsResponse.data.total_claims !== undefined) {
        toast.success('Dashboard data loaded successfully', { duration: 2000 });
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Use fallback data instead of showing error
      setStats(getDefaultStats());
      setRecentClaims(getDefaultClaims());
      setError('Backend connection pending');
      console.warn('Using fallback data for dashboard');
      toast.info('Displaying demo data - Backend connection pending', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const getDefaultStats = () => ({
    totalClaims: 0,
    pendingReview: 0,
    approvedClaims: 0,
    rejectedClaims: 0,
    disputedClaims: 0,
    totalVillages: 0,
    budgetLinked: 0,
    ocrAccuracy: 0
  });

  const getDefaultClaims = () => [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'under_review': return 'bg-blue-100 text-blue-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'disputed': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    // Clean approach to avoid any double currency symbols
    const cleanAmount = parseInt(amount);
    return '₹' + cleanAmount.toLocaleString('en-IN');
  };

  if (loading) {
    return <LoadingSpinner size="md" message={t('loadingDashboard')} submessage={t('fetchingLatestStatistics')} />;
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-0">
      {/* Backend Connection Warning */}
      {error && (
        <ErrorMessage 
          type="info"
          title={t('demoModeActive')}
          message={t('displayingSampleData')}
        />
      )}

      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white p-4 sm:p-6 rounded-lg shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold mb-2 text-orange-300">
              {t('welcome')}, {user?.full_name}
            </h1>
            <p className="text-sm sm:text-base text-blue-100">
              {user?.role && user.role.charAt(0).toUpperCase() + user.role.slice(1)} | {user?.department}
            </p>
            {user?.district && (
              <p className="text-blue-200 text-xs sm:text-sm mt-1">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                {user.district}, {user.state}
              </p>
            )}
          </div>
          <div className="text-left sm:text-right w-full sm:w-auto">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setLoading(true);
                  fetchDashboardData();
                }}
                className="bg-white/10 hover:bg-white/20 text-white border-white/30 text-xs sm:text-sm"
              >
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                {t('refresh')}
              </Button>
            </div>
            <p className="text-blue-200 text-xs sm:text-sm">{t('todaysDate')}</p>
            <p className="text-lg sm:text-xl font-semibold">
              {new Date().toLocaleDateString(
                currentLanguage === 'bn' ? 'bn-BD' : 
                currentLanguage === 'hi' ? 'hi-IN' : 
                currentLanguage === 'or' ? 'or-IN' : 
                currentLanguage === 'te' ? 'te-IN' : 'en-IN'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Key Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">{t('totalVillages')}</p>
                <p className="text-3xl font-bold text-blue-900">{stats?.total_villages || 0}</p>
                <p className="text-xs text-slate-500 mt-1">{t('coveredUnderFRA')}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <TreePine className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">{t('forestClaims')}</p>
                <p className="text-3xl font-bold text-green-700">{stats?.total_claims || 0}</p>
                <p className="text-xs text-slate-500 mt-1">{t('ifrCfrApplications')}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">{t('pendingReview')}</p>
                <p className="text-3xl font-bold text-orange-600">{stats?.pending_claims || 0}</p>
                <p className="text-xs text-slate-500 mt-1">{t('awaitingApproval')}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">{t('budgetLinked')}</p>
                <p className="text-2xl font-bold text-purple-700">
                  {stats?.total_budget_linked ? formatCurrency(stats.total_budget_linked) : '₹0'}
                </p>
                <p className="text-xs text-slate-500 mt-1">MGNREGA, PM-KISAN</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <IndianRupee className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Claims Overview */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <span>{t('claimsStatusOverview')}</span>
            </CardTitle>
            <CardDescription>{t('currentStatusForestRightsClaims')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">{t('approvedClaims')}</p>
                    <p className="text-sm text-green-600">{t('successfullyProcessed')}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-700">{stats?.approved_claims || 0}</p>
                  <p className="text-sm text-green-600">
                    {stats?.total_claims ? Math.round((stats.approved_claims / stats.total_claims) * 100) : 0}%
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Clock className="w-8 h-8 text-yellow-600" />
                  <div>
                    <p className="font-medium text-yellow-800">{t('pendingClaims')}</p>
                    <p className="text-sm text-yellow-600">{t('underProcessing')}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-yellow-700">{stats?.pending_claims || 0}</p>
                  <p className="text-sm text-yellow-600">
                    {stats?.total_claims ? Math.round((stats.pending_claims / stats.total_claims) * 100) : 0}%
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="w-8 h-8 text-orange-600" />
                  <div>
                    <p className="font-medium text-orange-800">{t('disputedClaims')}</p>
                    <p className="text-sm text-orange-600">{t('requireResolution')}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-orange-700">{stats?.disputed_claims || 0}</p>
                  <p className="text-sm text-orange-600">
                    {stats?.total_claims ? Math.round((stats.disputed_claims / stats.total_claims) * 100) : 0}%
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Health */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="w-5 h-5 text-green-600" />
              <span>{t('systemHealth')}</span>
            </CardTitle>
            <CardDescription>{t('platformPerformanceMetrics')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('ocrAccuracy')}</span>
              <Badge className="bg-green-100 text-green-800">
                {stats?.ocr_accuracy || 0}%
              </Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('schemesIntegrated')}</span>
              <Badge className="bg-blue-100 text-blue-800">
                {stats?.schemes_integrated || 0}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('databaseStatus')}</span>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-green-600">{t('connected')}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('lastSync')}</span>
              <span className="text-sm text-slate-600">{t('minutesAgo', { minutes: 2 })}</span>
            </div>

            <div className="pt-4 border-t">
              <Button className="w-full bg-blue-600 hover:bg-blue-700">
                <TrendingUp className="w-4 h-4 mr-2" />
                {t('viewFullAnalytics')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Claims */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span>{t('recentClaims')}</span>
            </div>
            <Button variant="outline" size="sm">
              <ExternalLink className="w-4 h-4 mr-2" />
              {t('viewAll')}
            </Button>
          </CardTitle>
          <CardDescription>{t('latestApplications')}</CardDescription>
        </CardHeader>
        <CardContent>
          {Array.isArray(recentClaims) && recentClaims.length > 0 ? (
            <div className="space-y-4">
              {recentClaims.map((claim) => (
                <div key={claim.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{claim.beneficiary_name}</p>
                      <p className="text-sm text-slate-600">{claim.village_name} | {claim.claim_type}</p>
                      <p className="text-xs text-slate-500">{t('areaLabel')}: {claim.area_claimed} {t('hectares')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={getStatusColor(claim.status)}>
                      {claim.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(claim.submitted_date).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState 
              icon={FileText}
              title={t('recentClaimsEmpty')}
              message={t('recentClaimsEmptyMessage')}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;